import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const PORTFOLIO_URL = "https://portfolio.greenline.app";

const HEADLINES = [
  ["Know your", "burn rate."],
  ["Track your", "runway."],
  ["Align your", "team."],
  ["Close your", "round."],
];

const FEATURES = [
  { tag: "RUNWAY", title: "Know exactly how long your cash lasts", desc: "Enter cash balance, MRR, and burn. Get a live countdown to zero, a cash-out date, and color-coded runway health." },
  { tag: "SMART_BUDGET", title: "Your budget, auto-computed", desc: "Greenline calculates max monthly spend from your cash and target runway — no manual entry, always in sync." },
  { tag: "RAISE_TIMELINE", title: "Know when to start your raise", desc: "Default alive or dead? When should you start fundraising? Target raise size? All computed live from your numbers." },
  { tag: "DEPARTMENTS", title: "Budget by team, not spreadsheet", desc: "Allocate budgets across Engineering, Marketing, Sales. Track headcount and spend vs. limit per department." },
  { tag: "INVESTOR_DASHBOARD", title: "Share a live view with investors", desc: "One click generates a read-only link. Investors see your runway, burn, and MRR — always live, never stale." },
  { tag: "RECURRING", title: "Recurring expenses on autopilot", desc: "Set up subscriptions and payroll once. Greenline auto-logs them each month on the day they're due." },
];

const TIERS = [
  {
    name: "STARTER", price: "$0", period: "/mo",
    desc: "Get a feel for the product.",
    features: ["Dashboard", "Transactions"],
    cta: "start free", highlight: false,
  },
  {
    name: "PRO", price: "$50", period: "/mo",
    desc: "For teams that need full financial clarity.",
    features: ["Everything in Starter", "Runway & burn tracker", "Smart budget", "Department budgets", "Recurring transactions", "Investor share link", "Spending groups", "Burn multiple tracker", "Team members & roles", "Priority support"],
    cta: "get started", highlight: true,
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [headlineIdx, setHeadlineIdx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setHeadlineIdx(i => (i + 1) % HEADLINES.length);
        setAnimating(false);
      }, 400);
    }, 3000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const [first, second] = HEADLINES[headlineIdx];

  return (
    <div style={{ minHeight: "100vh", background: "#000", color: "#fff", fontFamily: "'DM Mono', monospace", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 1; transform: translateY(0); }
          to   { opacity: 0; transform: translateY(16px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .headline-word {
          display: inline-block;
          animation: slideDown 0.4s ease both;
        }
        .headline-word.out {
          animation: slideUp 0.35s ease both;
        }
        .nav-link { color: #333; text-decoration: none; font-size: 11px; transition: color 0.15s; }
        .nav-link:hover { color: #888; }
        .feature-card { border: 1px solid #111; border-radius: 8px; padding: 20px; transition: border-color 0.15s, background 0.15s; cursor: default; }
        .feature-card:hover { border-color: #1f1f1f; background: #050505; }
        .tier-card { border: 1px solid #111; border-radius: 8px; padding: 24px; transition: border-color 0.15s; }
        .tier-card.highlight { border-color: #22c55e22; background: #040f04; }
        .cta-btn { background: #22c55e; color: #000; border: none; border-radius: 6px; padding: 10px 20px; font-size: 12px; font-family: 'DM Mono', monospace; font-weight: 500; cursor: pointer; transition: background 0.15s; }
        .cta-btn:hover { background: #16a34a; }
        .ghost-btn { background: transparent; color: #333; border: 1px solid #111; border-radius: 6px; padding: 10px 20px; font-size: 12px; font-family: 'DM Mono', monospace; cursor: pointer; transition: all 0.15s; }
        .ghost-btn:hover { border-color: #1f1f1f; color: #666; }
      `}</style>

      {/* Nav */}
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 40px", background: "rgba(0,0,0,0.9)", backdropFilter: "blur(12px)", borderBottom: "1px solid #0d0d0d" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 7, height: 7, borderRadius: 2, background: "#22c55e" }} />
          <span style={{ fontSize: 12, color: "#22c55e", fontWeight: 500, letterSpacing: "-0.01em" }}>Greenline</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <a href="#features" className="nav-link">features</a>
          <a href="#pricing" className="nav-link">pricing</a>
          <a href="/blog" className="nav-link">blog</a>
          <a href={PORTFOLIO_URL} target="_blank" rel="noopener noreferrer" className="nav-link" style={{ color: "#22c55e44" }}>for investors</a>
          <button className="ghost-btn" style={{ padding: "6px 14px", fontSize: 11 }} onClick={() => navigate("/login")}>sign in</button>
          <button className="cta-btn" style={{ padding: "6px 14px", fontSize: 11 }} onClick={() => navigate("/login")}>get started</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "120px 40px 80px", textAlign: "center" }}>

        {/* Animated headline */}
        <div style={{ marginBottom: 32, userSelect: "none" }}>
          {/* First half — black text on green */}
          <div style={{ display: "inline-block", background: "#22c55e", padding: "4px 14px 4px 14px", marginRight: 0 }}>
            <span
              key={`first-${headlineIdx}-${animating}`}
              className={`headline-word${animating ? " out" : ""}`}
              style={{ fontSize: "clamp(2.2rem, 6vw, 4.5rem)", fontWeight: 500, color: "#000", lineHeight: 1.1, letterSpacing: "-0.03em" }}
            >
              {first}
            </span>
          </div>
          {/* Second half — green text on black */}
          <div style={{ display: "inline-block", background: "#000", padding: "4px 14px", border: "1px solid #22c55e" }}>
            <span
              key={`second-${headlineIdx}-${animating}`}
              className={`headline-word${animating ? " out" : ""}`}
              style={{ fontSize: "clamp(2.2rem, 6vw, 4.5rem)", fontWeight: 500, color: "#22c55e", lineHeight: 1.1, letterSpacing: "-0.03em" }}
            >
              {second}
            </span>
          </div>
        </div>

        <p style={{ fontSize: 14, color: "#444", maxWidth: 480, lineHeight: 1.8, marginBottom: 36 }}>
          financial clarity for startups. track burn, runway, and department spend. generate investor-ready reports in seconds.
        </p>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center", marginBottom: 64 }}>
          <button className="cta-btn" onClick={() => navigate("/login")}>start for free →</button>
          <button className="ghost-btn" onClick={() => document.getElementById("features").scrollIntoView({ behavior: "smooth" })}>see features</button>
        </div>

        {/* Floating stat cards */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
          {[
            { label: "RUNWAY", value: "14mo", sub: "↑ healthy", color: "#22c55e" },
            { label: "MONTHLY_BURN", value: "$48k", sub: "↓ -8% mom", color: "#f59e0b" },
            { label: "MRR", value: "$12.4k", sub: "↑ +18% mom", color: "#3b82f6" },
          ].map(({ label, value, sub, color }) => (
            <div key={label} style={{ background: "#050505", border: "1px solid #111", borderRadius: 8, padding: "12px 16px", minWidth: 140, textAlign: "left", borderLeft: `2px solid ${color}33` }}>
              <div style={{ fontSize: 9, color: "#333", fontFamily: "var(--mono, 'DM Mono')", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 500, color, fontFamily: "'DM Mono', monospace" }}>{value}</div>
              <div style={{ fontSize: 9, color: "#333", marginTop: 3 }}>{sub}</div>
            </div>
          ))}
        </div>
      </section>


      {/* Features */}
      <section id="features" style={{ padding: "80px 40px", maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ marginBottom: 48 }}>
          <div style={{ fontSize: 9, color: "#22c55e", letterSpacing: "0.1em", marginBottom: 8 }}>FEATURES</div>
          <h2 style={{ fontSize: "clamp(1.4rem, 3vw, 2rem)", fontWeight: 500, letterSpacing: "-0.02em", color: "#888" }}>
            stop juggling spreadsheets.<br />
            <span style={{ color: "#fff" }}>start shipping.</span>
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 8 }}>
          {FEATURES.map(f => (
            <div key={f.tag} className="feature-card">
              <div style={{ fontSize: 9, color: "#22c55e55", letterSpacing: "0.08em", marginBottom: 10 }}>{f.tag}</div>
              <div style={{ fontSize: 13, color: "#888", fontWeight: 500, marginBottom: 6, fontFamily: "var(--sans, Inter)" }}>{f.title}</div>
              <div style={{ fontSize: 11, color: "#333", lineHeight: 1.8, fontFamily: "var(--sans, Inter)" }}>{f.desc}</div>
              {f.tag === "INVESTOR_DASHBOARD" && (
                <a
                  href={PORTFOLIO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-block", marginTop: 12, fontSize: 10, color: "#22c55e", textDecoration: "none", fontFamily: "'DM Mono', monospace" }}
                >
                  view investor portfolio →
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ padding: "80px 40px", maxWidth: 720, margin: "0 auto" }}>
        <div style={{ marginBottom: 48 }}>
          <div style={{ fontSize: 9, color: "#22c55e", letterSpacing: "0.1em", marginBottom: 8 }}>PRICING</div>
          <h2 style={{ fontSize: "clamp(1.4rem, 3vw, 2rem)", fontWeight: 500, letterSpacing: "-0.02em", color: "#888" }}>
            simple, transparent.<br />
            <span style={{ color: "#fff" }}>no surprises.</span>
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {TIERS.map(t => (
            <div key={t.name} className={`tier-card${t.highlight ? " highlight" : ""}`}>
              <div style={{ fontSize: 9, color: t.highlight ? "#22c55e" : "#333", letterSpacing: "0.08em", marginBottom: 10 }}>{t.name}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 6 }}>
                <span style={{ fontSize: 28, fontWeight: 500, color: t.highlight ? "#22c55e" : "#666" }}>{t.price}</span>
                <span style={{ fontSize: 11, color: "#333" }}>{t.period}</span>
              </div>
              <div style={{ fontSize: 11, color: "#333", marginBottom: 16, fontFamily: "var(--sans, Inter)", lineHeight: 1.6 }}>{t.desc}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
                {t.features.map(f => (
                  <div key={f} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ color: "#22c55e", fontSize: 9 }}>✓</span>
                    <span style={{ fontSize: 10, color: "#555", fontFamily: "var(--sans, Inter)" }}>{f.toLowerCase()}</span>
                  </div>
                ))}
              </div>
              <button
                className={t.highlight ? "cta-btn" : "ghost-btn"}
                style={{ width: "100%", fontSize: 11 }}
                onClick={() => navigate("/login")}
              >
                {t.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section style={{ padding: "80px 40px", textAlign: "center", borderTop: "1px solid #0d0d0d" }}>
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          {/* Split text CTA */}
          <div style={{ marginBottom: 16, display: "inline-flex", alignItems: "stretch" }}>
            <div style={{ background: "#22c55e", padding: "6px 16px", display: "flex", alignItems: "center" }}>
              <span style={{ fontSize: "clamp(1.2rem, 3vw, 1.8rem)", fontWeight: 500, color: "#000", letterSpacing: "-0.02em" }}>know your numbers.</span>
            </div>
            <div style={{ background: "#000", border: "1px solid #22c55e", padding: "6px 16px", display: "flex", alignItems: "center" }}>
              <span style={{ fontSize: "clamp(1.2rem, 3vw, 1.8rem)", fontWeight: 500, color: "#22c55e", letterSpacing: "-0.02em" }}>close your round.</span>
            </div>
          </div>
          <p style={{ fontSize: 12, color: "#333", marginBottom: 24, lineHeight: 1.8 }}>
            join founders using greenline to track burn, manage departments, and walk into investor meetings with confidence.
          </p>
          <button className="cta-btn" onClick={() => navigate("/login")}>get started free →</button>

          {/* Reviewed-by / featured badges */}
          <div style={{ marginTop: 32, display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
            <a
              href="https://bobstechreview.com/business-operations/greenline-budgeting"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 9,
                padding: "8px 10px", border: "1px solid #dbe5e2", borderRadius: 8,
                background: "#ffffff", color: "#10201f", textDecoration: "none",
                fontFamily: "Inter, Arial, sans-serif", boxShadow: "0 6px 18px rgba(20,43,40,0.10)",
              }}
            >
              <img
                src="https://bobstechreview.com/assets/btr-flat-logo-badge.jpg"
                alt="BTR" width="44" height="25"
                style={{ display: "block", width: 44, height: 25, objectFit: "contain", borderRadius: 4 }}
              />
              <span style={{ display: "grid", gap: 2, lineHeight: 1.1 }}>
                <strong style={{ fontSize: 12, color: "#10201f" }}>Reviewed by BTR</strong>
                <small style={{ fontSize: 10, color: "#087e75", fontWeight: 800, letterSpacing: ".02em" }}>Bob's Tech Review</small>
              </span>
            </a>
            <a
              href="https://startupfa.me/s/greenline?utm_source=greenlinebudgeting.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://startupfa.me/badges/featured-badge.webp"
                alt="Greenline Budgeting - Featured on Startup Fame"
                width="171" height="54"
                style={{ display: "block" }}
              />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: "1px solid #0d0d0d", padding: "16px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: 1, background: "#22c55e" }} />
          <span style={{ fontSize: 11, color: "#22c55e", fontWeight: 500 }}>Greenline</span>
        </div>
        <div style={{ display: "flex", gap: 20 }}>
          <a href="#features" className="nav-link" style={{ fontSize: 10 }}>features</a>
          <a href="#pricing"  className="nav-link" style={{ fontSize: 10 }}>pricing</a>
        </div>
        <span style={{ fontSize: 10, color: "#1a1a1a" }}>© 2026 Greenline</span>
      </footer>
    </div>
  );
}